import { useEffect, useState } from 'react';

import { useTranslation } from '@mint-lib/i18n';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogRoot,
  DialogTitle,
  Label,
  typographyVariants,
} from '@mint-lib/tw-components';

import type { ColumnOption } from '../../../api/queries/userColumnsConfig.js';
import RoleSelect from './RoleSelect.jsx';

type Props = {
  open: boolean;
  roleOptions: ColumnOption[];
  onClose: () => void;
  // Save wiring is in the follow-up ticket — for now this just closes the modal.
  onSave: (roleValue: string | number) => void;
};

const BulkEditModal = ({ open, roleOptions, onClose, onSave }: Props) => {
  const { t } = useTranslation('@myn/permissions');
  const [selectedRole, setSelectedRole] = useState<string | number | undefined>(
    undefined,
  );

  const [dropdownContainer, setDropdownContainer] =
    useState<HTMLDivElement | null>(null);

  useEffect(() => {
    if (open) setSelectedRole(undefined);
  }, [open]);

  const handleSave = () => {
    if (selectedRole === undefined) return;
    onSave(selectedRole);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) onClose();
      }}
    >
      <DialogPortal>
        <DialogOverlay />
        <DialogRoot size="s">
          <DialogHeader onClose={onClose}>
            <DialogTitle>{t('Edit role for selected users')}</DialogTitle>
          </DialogHeader>
          <DialogContent className="flex flex-col gap-4">
            <p
              className={typographyVariants({
                variant: 'bodyLong01',
                color: 'primary',
              })}
            >
              {t(
                'Choose a role to assign to all selected users. The selected role will determine their permissions and access level within the workspace.',
              )}
            </p>
            <div ref={setDropdownContainer} className="flex flex-col gap-1">
              <Label variant="label01">{t('Role')}</Label>
              <RoleSelect
                options={roleOptions}
                value={selectedRole}
                placeholder={t('Choose a role')}
                onChange={setSelectedRole}
                isPortal={false}
                container={dropdownContainer}
              />
            </div>
          </DialogContent>
          <DialogFooter>
            <Button variant="text" onClick={onClose}>
              {t('Cancel')}
            </Button>
            <Button
              color="primary"
              onClick={handleSave}
              disabled={selectedRole === undefined}
            >
              {t('Save')}
            </Button>
          </DialogFooter>
        </DialogRoot>
      </DialogPortal>
    </Dialog>
  );
};

export default BulkEditModal;
